import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck, Layers, PaintBucket, Droplets, Car,
  GraduationCap, Award, Briefcase, Users, Network, IndianRupee, CheckCircle2,
  Smartphone, Share2, CreditCard, BadgeCheck, Calculator, Volume2, Square } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useI18n, LangToggle } from "@/context/I18nContext";
import { api } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1920&q=80";

const CAT_ICON = {
  "ppf-applicator": Car, "glass-film-applicator": Layers, "vinyl-applicator": PaintBucket,
  "spf-applicator": ShieldCheck, "coating-applicator": Droplets,
};
const CAT_SHORT = {
  "ppf-applicator": "PPF APPLICATOR", "glass-film-applicator": "GLASS FILM APPLICATOR",
  "vinyl-applicator": "VINYL APPLICATOR", "spf-applicator": "SPF APPLICATOR",
  "coating-applicator": "COATING APPLICATOR",
};
const CAT_WORK = {
  "ppf-applicator": ["गाड़ी पर PPF लगाने का काम", "Paint protection film on vehicles"],
  "glass-film-applicator": ["कांच पर फिल्म लगाने का काम", "Film application on glass"],
  "vinyl-applicator": ["Vinyl लगाने का काम", "Vinyl wrapping & transformation"],
  "spf-applicator": ["Surface Protection Film लगाने का काम", "Surface protection film application"],
  "coating-applicator": ["Coating लगाने का काम", "Protective coating application"],
};
const SKILL_CHIPS = ["PPF", "GLASS FILM", "VINYL", "SPF", "COATING"];

const speechSupported = () => typeof window !== "undefined" && "speechSynthesis" in window;

function ListenBtn({ id, text, speakingId, onSpeak, label }) {
  if (!speechSupported()) return null;
  const on = speakingId === id;
  return (
    <button onClick={() => onSpeak(id, text)} data-testid={`listen-${id}`}
      aria-label={label.play}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-full border shrink-0 transition-colors ${on ? "bg-[#C9A45C] text-black border-[#C9A45C] animate-pulse" : "border-[#C9A45C]/50 text-[#C9A45C] hover:bg-[#C9A45C]/10"}`}>
      {on ? <Square size={13} /> : <Volume2 size={15} />} {on ? label.stop : label.play}
    </button>
  );
}

export default function ApplicatorNetwork() {
  const navigate = useNavigate();
  const { lang, setLang } = useI18n();
  const [cats, setCats] = useState([]);
  const hi = lang !== "en";
  const L = (h, e) => (hi ? h : e);

  useEffect(() => { setLang("hi"); /* Hindi is the dominant language for this audience */ }, []); // eslint-disable-line
  useEffect(() => {
    api.get("/applicator/services/catalogue")
      .then(({ data }) => setCats([...data].sort((a, b) => (a.order || 9) - (b.order || 9))))
      .catch(() => setCats([]));
  }, []);

  const goApply = () => navigate("/applicator/apply");
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  // ---- "सुनो" read-aloud (browser speech synthesis, Hindi voice, no external service) ----
  const [speakingId, setSpeakingId] = useState(null);
  useEffect(() => () => { if (speechSupported()) window.speechSynthesis.cancel(); }, []);
  const speak = (id, text) => {
    if (!speechSupported()) return;
    const synth = window.speechSynthesis;
    if (speakingId === id) { synth.cancel(); setSpeakingId(null); return; }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = hi ? "hi-IN" : "en-IN";
    u.rate = 0.92;
    const voices = synth.getVoices() || [];
    const v = voices.find((x) => x.lang === u.lang) || voices.find((x) => (x.lang || "").startsWith(hi ? "hi" : "en"));
    if (v) u.voice = v;
    u.onend = () => setSpeakingId(null);
    u.onerror = () => setSpeakingId(null);
    setSpeakingId(id);
    synth.speak(u);
  };
  const listen = { play: L("सुनो", "Listen"), stop: L("रोको", "Stop") };

  const EARN = [
    { icon: Briefcase, n: "1", t: L("अपना काम करो", "Do your own job"),
      d: L("ERRĀH का ग्राहक मिला। आपने काम किया। काम पूरा हुआ। आपको कमाई मिली।", "You get an ERRĀH customer, do the job, complete it, and earn."),
      tag: L("₹ आपकी कमाई", "₹ Your earning") },
    { icon: Share2, n: "2", t: L("ग्राहक भेजो", "Refer a customer"),
      d: L("आपके पास ग्राहक है? ERRĀH को बताओ। काम पूरा होने पर आपको रेफरल कमाई मिलेगी।", "Have a customer? Tell ERRĀH. Earn a referral once the job completes."),
      tag: L("₹ रेफरल कमाई", "₹ Referral earning") },
    { icon: Users, n: "3", t: L("अपनी टीम बनाओ", "Build your team"),
      d: L("अपने जैसे PPF, Glass Film, Vinyl, SPF या Coating Applicator को ERRĀH से जोड़ो। उनकी असली बिक्री पर आपको नेटवर्क कमाई मिल सकती है।", "Bring other PPF, Glass Film, Vinyl, SPF or Coating applicators to ERRĀH. Earn network income on their genuine sales."),
      tag: L("₹ नेटवर्क कमाई", "₹ Network earning") },
  ];

  const JOBSTEPS = [
    L("ग्राहक ने काम बुक किया", "Customer books a job"),
    L("ERRĀH देखेगा कि कौन सा काम है", "ERRĀH identifies the service"),
    L("जिस Applicator को वह काम आता है, उसे काम भेजा जाएगा", "The job goes to a certified applicator for that work"),
    L("काम करो → काम पूरा करो → कमाई पाओ", "Do the job → complete it → get paid"),
  ];

  const BENEFITS = [
    [GraduationCap, L("ट्रेनिंग", "Training")], [Award, L("सर्टिफिकेट", "Certificate")],
    [Users, L("ग्राहक", "Customers")], [Briefcase, L("काम", "Jobs")],
    [Smartphone, L("मोबाइल ऐप", "Mobile app")], [Calculator, L("कमाई का हिसाब", "Earnings record")],
    [Share2, L("रेफरल", "Referrals")], [Network, L("टीम बनाने का मौका", "Build a team")],
    [CreditCard, L("ऑनलाइन भुगतान", "Online payout")], [BadgeCheck, L("ERRĀH ब्रांड", "ERRĀH brand")],
  ];

  const FAQ = [
    [L("जुड़ने के लिए पैसे लगेंगे?", "Is there a joining fee?"),
     L("नहीं। ERRĀH से जुड़ना फ्री है। मोबाइल OTP से रजिस्टर करो।", "No. Joining ERRĀH is free. Register with mobile OTP.")],
    [L("सिर्फ आदमी जोड़ने पर पैसा मिलेगा?", "Do I earn just by adding people?"),
     L("नहीं। सिर्फ किसी को जोड़ने पर पैसा नहीं मिलता। कमाई असली ERRĀH बिक्री/काम से जुड़ी है।", "No. You don't earn just by adding members. Earnings are linked to real ERRĀH sales/work.")],
    [L("मुझे काम कैसे मिलेगा?", "How will I get work?"),
     L("जिस काम का सर्टिफिकेट होगा, उसी के योग्य जॉब आपके इलाके, रेटिंग और उपलब्धता के हिसाब से मिलेंगे।", "You get eligible jobs for your certified skill, based on your area, rating and availability.")],
    [L("मुझे ट्रेनिंग मिलेगी?", "Will I get training?"),
     L("हाँ। ERRĀH हर काम की ट्रेनिंग और सर्टिफिकेट देगा।", "Yes. ERRĀH provides training and certification for each skill.")],
    [L("एक से ज्यादा काम सीख सकता हूं?", "Can I learn more than one skill?"),
     L("हाँ। जैसे PPF + Vinyl, या SPF + Coating — या पाँचों।", "Yes. Like PPF + Vinyl, or SPF + Coating — or all five.")],
    [L("पैसा कब मिलेगा?", "When do I get paid?"),
     L("काम पूरा और QC पास होने के बाद, ERRĀH admin द्वारा verify करके भुगतान होता है।", "After the job is completed and QC-passed, payout is made once verified by ERRĀH admin.")],
    [L("मेरे पास ग्राहक है, क्या मैं ERRĀH को दे सकता हूं?", "I have a customer — can I give them to ERRĀH?"),
     L("हाँ। ग्राहक भेजो। काम पूरा होने पर रेफरल कमाई मिलेगी।", "Yes. Refer the customer. Earn a referral when the job completes.")],
  ];

  const say = {
    hero: L("अपनी कला से अपना काम बढ़ाओ। ERRĀH के साथ काम करो। ग्राहक पाओ। कमाई करो। अपनी टीम बनाओ।",
            "Turn your skill into your own business. Work with ERRĀH. Get customers. Earn. Build your team."),
    sec1: L("आप कौन सा काम जानते हैं? अपना काम चुनें, ERRĀH आपको ट्रेनिंग और सर्टिफिकेट देगा।",
            "Which work do you know? Choose your skill; ERRĀH gives you training and a certificate.")
          + " " + cats.map((c) => (CAT_WORK[c.service_id] || ["", ""])[hi ? 0 : 1]).join("। "),
    earn: L("कमाई के तीन तरीके।", "Three ways to earn.") + " " + EARN.map((e) => e.t + "। " + e.d).join(" ")
          + " " + L("सिर्फ किसी को जोड़ने पर पैसा नहीं मिलेगा। कमाई असली ERRĀH बिक्री या काम से जुड़ी है।",
                    "You will not earn just by adding someone. Earnings are linked to real ERRĀH sales or work."),
    team: L("अपनी टीम कैसे बनाएं? आप, फिर आपकी टीम, फिर आपकी टीम के लोग, फिर बड़ा ERRĀH नेटवर्क।",
            "How to build your team. You, then your team, then their team, then a big ERRĀH network."),
    howjobs: L("काम कैसे मिलेगा?", "How you get work.") + " " + JOBSTEPS.join("। ")
          + " " + L("सिर्फ सर्टिफाइड applicator को ही योग्य जॉब मिलेंगे।", "Only certified applicators receive eligible jobs."),
    training: L("पहले सीखो, फिर काम पाओ। पहले ट्रेनिंग, फिर सर्टिफिकेट, फिर एक्टिव, फिर काम। जिस काम का सर्टिफिकेट होगा, उसी काम के योग्य जॉब मिलेंगे।",
            "Learn first, then work. First training, then certificate, then active, then work. You get eligible jobs only for the skill you are certified in."),
    example: L("एक काम से कमाई कैसे होती है? यह सिर्फ उदाहरण है। काम की कीमत बीस हज़ार रुपये। आपकी कमाई पाँच हज़ार रुपये। ग्राहक रेफरल एक हज़ार रुपये। नेटवर्क कमाई चार सौ रुपये। असल कमाई काम, उत्पाद और जगह के अनुसार होगी।",
            "How earning works on one job. This is only an example. Job value twenty thousand rupees. Your earning five thousand. Customer referral one thousand. Network earning four hundred. Actual earnings depend on the job, product and location."),
    benefits: L("ERRĀH आपके लिए क्या करेगा?", "What ERRĀH gives you.") + " " + BENEFITS.map((b) => b[1]).join("। "),
    whofor: L("किसके लिए है? अगर आपको PPF, Glass Film, Vinyl, SPF या Coating का काम आता है, तो ERRĀH के साथ जुड़ सकते हैं।",
            "Who is it for? If you know PPF, Glass Film, Vinyl, SPF or Coating work, you can join ERRĀH."),
    faq: L("आम सवाल।", "Common questions.") + " " + FAQ.map((f) => f[0] + " " + f[1]).join(" "),
  };

  return (
    <div className="bg-[#080808] min-h-screen pb-24 sm:pb-0" data-testid="applicator-network-page">
      <Seo title="ERRĀH Applicator Network" description="अपनी कला से अपना काम बढ़ाओ। ERRĀH के साथ काम करो, ग्राहक पाओ, कमाई करो, अपनी टीम बनाओ।" />

      {/* HEADER */}
      <div className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-[1500px] mx-auto px-4 lg:px-10 h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/errah-logo.png" alt="ERRĀH" className="h-8 w-auto" />
            <span className="hidden sm:flex flex-col leading-none"><span className="font-serif text-lg tracking-[0.25em]">ERRĀH</span><span className="text-[9px] tracking-[0.2em] text-gray-500">PROTECT WHAT MATTERS</span></span>
          </Link>
          <div className="flex items-center gap-2.5">
            <LangToggle />
            <button onClick={() => navigate("/applicator/login")} data-testid="nav-applicator-login" className="text-sm text-gray-300 hover:text-white px-2">{L("लॉगिन", "Login")}</button>
            <button onClick={goApply} data-testid="nav-join" className="bg-[#C9A45C] text-black px-4 py-2 text-xs font-semibold tracking-wide hover:bg-[#E4C987] transition-colors">{L("आवेदन करें", "Apply")}</button>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden">
        <img src={HERO} alt={L("ERRĀH एप्लिकेटर काम करते हुए", "ERRĀH applicator at work")} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/40" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 w-full pb-16 lg:pb-24">
          <motion.h1 initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} className="serif-head text-4xl sm:text-5xl lg:text-7xl max-w-4xl">{L("अपनी कला से अपना काम बढ़ाओ", "Turn your skill into your own business")}</motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="mt-5 text-lg sm:text-2xl text-gray-100 font-medium leading-relaxed">
            {L("ERRĀH के साथ काम करो। ग्राहक पाओ। कमाई करो। अपनी टीम बनाओ।", "Work with ERRĀH. Get customers. Earn. Build your team.")}
          </motion.p>
          <div className="mt-6 flex flex-wrap gap-2">
            {SKILL_CHIPS.map((s) => <span key={s} className="text-xs sm:text-sm border border-[#C9A45C]/40 text-[#E4C987] px-3 py-1.5 rounded-sm">{s}</span>)}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={goApply} data-testid="hero-join-cta" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-8 py-4 text-base font-bold tracking-wide hover:bg-[#E4C987] hover:-translate-y-0.5 transition-all">{L("आवेदन करें", "Apply now")} <ArrowRight size={18} /></button>
            <button onClick={() => scrollTo("how-jobs")} data-testid="hero-how-cta" className="border border-white/30 px-8 py-4 text-base font-semibold tracking-wide hover:bg-white hover:text-black transition-colors">{L("कैसे काम करता है?", "How it works")}</button>
            <ListenBtn id="hero" text={say.hero} speakingId={speakingId} onSpeak={speak} label={listen} />
          </div>
        </div>
      </section>

      {/* SECTION 1 — choose your skill */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="applicator-categories">
        <Reveal>
          <h2 className="serif-head text-3xl lg:text-5xl mb-2">{L("आप कौन सा काम जानते हैं?", "Which work do you know?")}</h2>
          <p className="text-gray-300 mb-4 text-base lg:text-lg">{L("अपना काम चुनें — ERRĀH आपको ट्रेनिंग और सर्टिफिकेट देगा।", "Choose your skill — ERRĀH gives you training and a certificate.")}</p>
          <div className="mb-10"><ListenBtn id="sec1" text={say.sec1} speakingId={speakingId} onSpeak={speak} label={listen} /></div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {cats.map((c, i) => {
            const Icon = CAT_ICON[c.service_id] || ShieldCheck;
            const work = CAT_WORK[c.service_id] || ["", ""];
            return (
              <Reveal key={c.service_id} delay={(i % 5) * 0.06}>
                <button onClick={goApply} data-testid={`applicator-cat-${c.service_id}`}
                  className="group w-full text-left hair-border bg-white/[0.02] p-6 h-full hover:border-[#C9A45C]/60 hover:-translate-y-1 transition-all">
                  <Icon className="text-[#C9A45C]" size={40} />
                  <p className="font-display text-base mt-4 tracking-wide">{CAT_SHORT[c.service_id] || c.name}</p>
                  <p className="text-sm text-gray-300 mt-2 leading-snug">{L(work[0], work[1])}</p>
                  <span className="inline-flex items-center gap-1 text-xs gold-text mt-4">{L("चुनें", "Choose")} <ArrowRight size={12} /></span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SECTION 2 — 3 ways to earn */}
      <section className="border-y border-white/10 bg-warmblack">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="ways-to-earn">
          <Reveal><div className="flex flex-wrap items-center gap-4 mb-10"><h2 className="serif-head text-3xl lg:text-5xl">{L("कमाई के 3 तरीके", "3 ways to earn")}</h2><ListenBtn id="earn" text={say.earn} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {EARN.map((e, i) => (
              <Reveal key={e.n} delay={i * 0.08}>
                <div className="hair-border bg-white/[0.02] p-7 h-full flex flex-col">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-5xl gold-text">{e.n}</span>
                    <e.icon className="text-[#C9A45C]" size={30} />
                  </div>
                  <p className="font-display text-xl mt-4">{e.t}</p>
                  <p className="text-base text-gray-300 mt-3 leading-relaxed flex-1">{e.d}</p>
                  <span className="inline-flex items-center gap-1.5 mt-5 text-lg font-bold text-[#E4C987]"><IndianRupee size={18} />{e.tag.replace("₹ ", "")}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 hair-border border-[#C9A45C]/40 bg-[#C9A45C]/[0.06] p-5" data-testid="no-recruitment-note">
            <p className="text-lg font-bold text-[#E4C987]">{L("सिर्फ किसी को जोड़ने पर पैसा नहीं मिलेगा।", "You will NOT earn just by adding someone.")}</p>
            <p className="text-sm text-gray-300 mt-1.5">{L("कमाई असली ERRĀH बिक्री/काम से जुड़ी है।", "Earnings are linked to real ERRĀH sales/work.")}</p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — build your team */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="network-structure">
        <Reveal><div className="flex flex-wrap items-center gap-4 mb-10"><h2 className="serif-head text-3xl lg:text-5xl">{L("अपनी टीम कैसे बनाएं?", "How to build your team")}</h2><ListenBtn id="team" text={say.team} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
        <div className="max-w-2xl mx-auto text-center">
          {[
            [L("आप", "You"), "bg-[#C9A45C] text-black"],
            [L("आपकी टीम", "Your team"), "border border-white/20 text-gray-100"],
            [L("आपकी टीम के लोग", "Their team"), "border border-white/15 text-gray-300"],
            [L("बड़ा ERRĀH नेटवर्क", "Big ERRĀH network"), "border border-[#C9A45C]/40 text-[#E4C987]"],
          ].map(([label, cls], i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className={`inline-block px-6 py-3 text-base font-semibold ${cls}`}>{label}</div>
              {i < 3 && <ArrowDown className="mx-auto my-2.5 text-gray-600" size={22} />}
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {SKILL_CHIPS.map((s) => <span key={s} className="text-xs border border-white/15 px-3 py-1.5 rounded-sm text-gray-300">{s} Applicator</span>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — how you get work */}
      <section id="how-jobs" className="border-y border-white/10 bg-warmblack">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="how-jobs">
          <Reveal><div className="flex flex-wrap items-center gap-4 mb-10"><h2 className="serif-head text-3xl lg:text-5xl">{L("काम कैसे मिलेगा?", "How you get work")}</h2><ListenBtn id="howjobs" text={say.howjobs} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {JOBSTEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div className="hair-border bg-white/[0.02] p-6 h-full">
                  <span className="font-display text-5xl gold-text">{i + 1}</span>
                  <p className="text-base text-gray-200 mt-3 leading-relaxed">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-400"><CheckCircle2 className="inline text-[#C9A45C] mr-1.5" size={16} />{L("सिर्फ ACTIVE / CERTIFIED applicator को ही योग्य जॉब मिलेंगे।", "Only ACTIVE / CERTIFIED applicators receive eligible jobs.")}</p>
        </div>
      </section>

      {/* SECTION 5 — learn first, then work */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="training-cert">
        <Reveal><div className="flex flex-wrap items-center gap-4 mb-8"><h2 className="serif-head text-3xl lg:text-5xl">{L("पहले सीखो, फिर काम पाओ", "Learn first, then work")}</h2><ListenBtn id="training" text={say.training} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
        <div className="flex flex-wrap items-center gap-2 text-sm mb-8">
          {[L("काम नहीं आता", "Not skilled"), "TRAINING", "CERTIFICATION", "ACTIVE", L("काम", "Work")].map((s, i, a) => (
            <span key={s} className="flex items-center gap-2">
              <span className={`px-3 py-2 border text-sm ${i >= 3 ? "border-[#C9A45C] text-[#C9A45C]" : "border-white/15 text-gray-200"}`}>{s}</span>
              {i < a.length - 1 && <ArrowRight size={14} className="text-gray-600" />}
            </span>
          ))}
        </div>
        <p className="text-base text-gray-300 mb-6">{L("जिस काम का सर्टिफिकेट होगा, उसी काम के योग्य जॉब मिलेंगे।", "You get eligible jobs only for the skill you are certified in.")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {SKILL_CHIPS.map((s, i) => (
            <Reveal key={s} delay={(i % 5) * 0.05}>
              <div className="hair-border bg-white/[0.02] p-4 text-center">
                <p className="text-sm font-semibold text-gray-100">{s} Certified</p>
                <ArrowDown className="mx-auto my-2 text-gray-600" size={16} />
                <p className="text-sm gold-text">{s} {L("जॉब", "jobs")}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SECTION 6 — earning example */}
      <section className="border-y border-white/10 bg-warmblack">
        <div className="max-w-[900px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="earning-example">
          <Reveal><h2 className="serif-head text-3xl lg:text-5xl mb-2">{L("एक काम से कमाई कैसे होती है?", "How earning works on one job")}</h2>
            <p className="text-sm text-[#E4C987] font-semibold mb-4">{L("सिर्फ उदाहरण", "Example only")}</p>
            <div className="mb-8"><ListenBtn id="example" text={say.example} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
          <div className="space-y-3">
            {[[L("काम की कीमत", "Job value"), "₹20,000", "text-gray-100"],
              [L("आपका काम / कमाई", "Your work / earning"), "₹5,000", "text-[#E4C987]"],
              [L("ग्राहक रेफरल", "Customer referral"), "₹1,000", "text-[#E4C987]"],
              [L("नेटवर्क कमाई", "Network earning"), "₹400", "text-[#E4C987]"]].map(([label, amt, cls], i, a) => (
              <Reveal key={label} delay={i * 0.07}>
                <div className="hair-border bg-white/[0.02] p-5 flex items-center justify-between">
                  <span className="text-base text-gray-300">{label}</span>
                  <span className={`font-display text-2xl ${cls}`}>{amt}</span>
                </div>
                {i < a.length - 1 && <ArrowDown className="mx-auto my-1 text-gray-600" size={18} />}
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-400">{L("असल कमाई काम, उत्पाद, जगह और ERRĀH की लागू कमाई योजना के अनुसार होगी।", "Actual earnings depend on the job, product, location and ERRĀH's applicable earning plan.")}</p>
        </div>
      </section>

      {/* SECTION 7 — what ERRAH gives you */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="benefits">
        <Reveal><div className="flex flex-wrap items-center gap-4 mb-10"><h2 className="serif-head text-3xl lg:text-5xl">{L("ERRĀH आपके लिए क्या करेगा?", "What ERRĀH gives you")}</h2><ListenBtn id="benefits" text={say.benefits} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {BENEFITS.map(([Icon, label], i) => (
            <Reveal key={i} delay={(i % 5) * 0.05}>
              <div className="hair-border bg-white/[0.02] p-5 text-center h-full flex flex-col items-center justify-center gap-3">
                <Icon className="text-[#C9A45C]" size={30} />
                <p className="text-sm text-gray-200 font-medium">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SECTION 8 — who is it for */}
      <section className="border-y border-white/10 bg-warmblack">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="who-for">
          <Reveal><h2 className="serif-head text-3xl lg:text-5xl mb-4">{L("किसके लिए है?", "Who is it for?")}</h2>
            <p className="text-base text-gray-300 mb-4">{L("अगर आपको इनमें से कोई काम आता है, तो ERRĀH के साथ जुड़ सकते हैं।", "If you know any of these skills, you can join ERRĀH.")}</p>
            <div className="mb-8"><ListenBtn id="whofor" text={say.whofor} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
          <div className="flex flex-wrap gap-3">
            {cats.map((c) => (
              <span key={c.service_id} className="inline-flex items-center gap-2 border border-[#C9A45C]/40 text-[#E4C987] px-4 py-2.5 text-sm">
                {(() => { const I = CAT_ICON[c.service_id] || ShieldCheck; return <I size={16} />; })()}
                {CAT_SHORT[c.service_id] || c.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — FAQ */}
      <section className="max-w-[900px] mx-auto px-5 lg:px-10 py-16 lg:py-20" data-testid="faq">
        <Reveal><div className="flex flex-wrap items-center gap-4 mb-10"><h2 className="serif-head text-3xl lg:text-5xl">{L("आम सवाल", "Common questions")}</h2><ListenBtn id="faq" text={say.faq} speakingId={speakingId} onSpeak={speak} label={listen} /></div></Reveal>
        <div className="space-y-3">
          {FAQ.map(([q, a], i) => (
            <Reveal key={i} delay={(i % 5) * 0.04}>
              <details className="hair-border bg-white/[0.02] p-5 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer text-base font-medium text-gray-100 list-none flex items-center justify-between gap-3">
                  {q} <ArrowDown size={16} className="text-gray-500 group-open:rotate-180 transition-transform shrink-0" />
                </summary>
                <p className="mt-3 text-base text-gray-300 leading-relaxed">{a}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-20 text-center">
          <Reveal>
            <h2 className="serif-head text-4xl lg:text-6xl">{L("अपनी कला को कमाई में बदलो", "Turn your skill into income")}</h2>
            <p className="mt-5 text-lg gold-text tracking-wide">PPF | GLASS FILM | VINYL | SPF | COATING</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button onClick={goApply} data-testid="final-join-cta" className="inline-flex items-center gap-2 bg-[#C9A45C] text-black px-9 py-4 text-base font-bold tracking-wide hover:bg-[#E4C987] transition-colors">{L("अभी आवेदन करें", "Apply now")} <ArrowRight size={18} /></button>
              <button onClick={goApply} data-testid="final-team-cta" className="border border-white/30 px-9 py-4 text-base font-semibold tracking-wide hover:bg-white hover:text-black transition-colors">{L("अपनी टीम बनाएं", "Build your team")}</button>
            </div>
            <p className="mt-10 text-xs text-gray-500 max-w-2xl mx-auto leading-relaxed">{L("ERRĀH नेटवर्क में कमाई असली उत्पाद/सेवा बिक्री से जुड़ी है। सिर्फ सदस्य जोड़ने पर कमाई नहीं होती। कमाई की कोई गारंटी नहीं है।", "In the ERRĀH network, earnings are linked to genuine product/service sales. You do not earn merely by adding members. There is no guarantee of income.")}</p>
          </Reveal>
        </div>
      </section>

      {/* sticky mobile CTA */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-50 bg-[#0A0A0A] border-t border-[#C9A45C]/20 p-3">
        <button onClick={goApply} data-testid="sticky-join" className="w-full bg-[#C9A45C] text-black py-3.5 text-base font-bold tracking-wide">{L("अभी आवेदन करें", "Apply now")}</button>
      </div>
    </div>
  );
}
